import { createContext, useContext } from 'react';
import type { BoardDto } from '../../api/boards';

/**
 * 게시판 목록을 셸과 화면이 **한 번만 읽어 함께 쓰는** 통로 (2026-09-17).
 *
 * ## 왜
 *
 * 좌측 메뉴(트리)가 셸로 올라가면서 목록을 읽는 곳이 둘이 될 뻔했다 — 셸이 트리를 그리려고 한 번,
 * 목록 화면이 「지금 게시판」을 찾으려고 또 한 번. 그러면 ①같은 요청이 화면 이동마다 두 번 나가고
 * ②「모두 읽음」 뒤에 한쪽만 다시 읽어 **트리의 안 읽음 배지가 목록과 어긋난다**.
 *
 * 그래서 목록은 **셸이 혼자 읽고**(진실의 한 곳), 화면은 여기서 받아 쓴다. 다시 세야 할 일이
 * 생기면(`모두 읽음` · 휴지통 되살리기 · 다시 읽기) 화면이 `recount()` 를 부르고,
 * 셸이 다시 읽어 **트리와 본문이 같은 값으로 함께** 갱신된다.
 */
export type BoardsState =
  | { status: 'loading' }
  | { status: 'ready'; boards: BoardDto[]; canCreate: boolean }
  | { status: 'error'; message: string };

export interface BoardsContextValue {
  state: BoardsState;
  /** 목록을 다시 읽는다 — 트리·본문이 **함께** 새 값을 받는다. */
  recount: () => void;
  /** 다시 읽을 때마다 오르는 수. 목록에 딸린 다른 질의(요약 등)를 같이 맞출 때 쓴다. */
  version: number;
  /**
   * 「지금 어느 게시판을 보고 있나」를 화면이 셸에 알린다.
   *
   * ★ 왜 필요한가 — 글 상세 주소(`/board/post/:postId`)에는 **게시판 id 가 없다**. 그래서 좌측
   *   메뉴가 「어디에 있는지」를 못 그렸다(아무것도 안 골라진 채였다). 글을 받아 보면 그 글의
   *   게시판을 알게 되므로, 그때 여기로 알려 좌측이 제자리를 표시하게 한다.
   * ★ 화면을 떠날 때 `null` 로 되돌린다 — 안 그러면 다른 곳에 가서도 옛 게시판이 골라져 있다.
   */
  setViewingBoardId: (boardId: string | null) => void;
}

const FALLBACK: BoardsContextValue = {
  state: { status: 'loading' },
  recount: () => { /* 셸 밖에서는 다시 셀 것이 없다 */ },
  version: 0,
  setViewingBoardId: () => { /* 셸 밖에서는 알릴 곳이 없다 */ },
};

export const BoardsContext = createContext<BoardsContextValue>(FALLBACK);

/** 셸 안에서 쓰면 셸이 읽은 목록을, 밖이면 「읽는 중」을 준다(시험이 화면만 따로 띄울 때). */
export function useBoards(): BoardsContextValue {
  return useContext(BoardsContext);
}
